import { useEffect, useState } from 'react';
import type { DamageMatrixPayload } from '../../server/routes/dashboards.ts';
import { CitationBadge } from '../components/CitationBadge.tsx';
import { DamageMatrixGrid, MatrixLegend } from '../components/DamageMatrixGrid.tsx';
import { LeadPredictionCard } from '../components/LeadPredictionCard.tsx';
import { SpeedTierTable } from '../components/SpeedTierTable.tsx';
import { Sprite } from '../components/Sprite.tsx';
import { TeamCard } from '../components/TeamCard.tsx';
import { TypeChip } from '../components/TypeChip.tsx';
import { ACTIVE_REGULATION, MONS, SUGGESTED_PROMPTS } from '../mock/data.ts';
import type { SpeciesId } from '../types.ts';

type SavedTeamRef = { id: number; name: string; regulationId: string | null };
type DashboardLeadPair = {
  ok: true;
  regulation_id: string;
  yours: { species: string; label: string; moves: string[] }[];
  theirs: { species: string; label: string; top_move?: string; top_move_pct?: number }[];
  opp_sets: {
    species: string;
    item: string | null;
    ability: string | null;
    nature: string | null;
    ev_spread: string | null;
    usage_pct: number | null;
    moves: { move: string; pct: number }[];
  }[];
  first_moves: {
    species: string;
    total_appearances: number;
    moves: { move: string; count: number; fraction: number }[];
  }[];
  matrix_offence: DamageMatrixPayload;
  matrix_defence: DamageMatrixPayload;
  speed_order: { species: string; label: string; final_speed: number; side: 'yours' | 'theirs' }[];
  annotated_replays: {
    replay_url: string;
    summary: string;
    matchup_takeaway: string | null;
    winning_archetype: string | null;
    quality_rating: number | null;
    led_by: 'p1' | 'p2' | null;
  }[];
  opp_picker_options: { species: string; usage_pct: number | null }[];
};
// DamageCell + DamageMatrixPayload imported at the top of this file from the
// server's contract (re-exported from src/calc/dashboard-helpers.ts).
export type DashboardTeam = {
  ok: true;
  regulation_id: string;
  team: { id: number; name: string; showdown_export: string; members: any[] };
  kpis: {
    similar_teams_in_corpus: number;
    replays_studied: number;
    exact_matchups: number;
  };
  predicted_leads: {
    matchups_used: number;
    exact_matchups: number;
    predictions: { lead_pair: [string, string]; count: number; fraction_of_matchups: number }[];
  };
  speed_tier: { rows: any[]; team_species: string[] };
  damage_matrix_your_team_vs_meta: DamageMatrixPayload;
  damage_matrix_meta_vs_your_team: DamageMatrixPayload;
  ohko_threats: { attacker: string; move: string; defender: string }[];
  similar_teams: {
    teams_considered: number;
    teams_matched: number;
    precedents: {
      teamId: number;
      sharedSpecies: number;
      placement: number | null;
      playerHandle: string | null;
      tournamentName: string | null;
      tournamentDate: string | null;
      sourceUrl: string | null;
    }[];
  };
};

// Dashboard A — meta overview at /meta. Cost-deflection surface: every panel
// here is a chat run we don't pay for. KPI strip + top leads (ladder vs
// tournament-team frequency) + recent tournaments + cited clips. Still no
// most-common-teams panel (needs team clustering; deferred until the data
// supports it).

type LeadRow = { species: string; count: number; fraction: number };
type TournamentRow = {
  id: string;
  name: string;
  date: string;
  winner_handle: string | null;
  winner_country: string | null;
  total_players: number | null;
  source_url: string;
};
type ClipRow = {
  id: number;
  claim_type: string;
  quote: string;
  channel_name: string | null;
  video_title: string | null;
  source_url: string | null;
  source_timestamp_sec: number | null;
  pokemon_referenced: string[];
  created_at: string;
};
type DashboardMeta = {
  ok: true;
  regulation_id: string;
  kpis: {
    replays_indexed: number;
    tournament_entries: number;
    top_lead: { species: string; pct: number; count: number; of: number } | null;
    top_lead_pair: {
      pair: [string, string];
      pct: number;
      count: number;
      of: number;
    } | null;
  };
  filters: { source: 'showdown' | 'tournaments'; days: number | null };
  top_leads: {
    available: boolean;
    source: 'pokemon-showdown';
    n_label: string;
    results: LeadRow[];
  };
  top_overall: {
    source: 'pokemon-showdown' | 'limitless';
    n_label: string;
    results: LeadRow[];
  };
  speed_tier: { rows: SpeedRow[] };
  recent_tournaments: TournamentRow[];
  cited_clips: ClipRow[];
};

type SpeedRow = {
  species: string;
  common_spread: string;
  final_speed: number;
  outpaces_me: boolean;
  usage_pct: number;
  base_speed: number;
  default_nature: string | null;
  default_ev_spe: number;
  variant_nature: string | null;
  variant_final_speed: number | null;
};

export function MetaDashboard() {
  const [data, setData] = useState<DashboardMeta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [source, setSource] = useState<'showdown' | 'tournaments'>('showdown');
  const [days, setDays] = useState<0 | 7 | 30 | 90>(0);
  // Tab state. 'meta' = page-level dashboard (default), or a team id when the
  // user picks one of their saved teams from the tab bar.
  const [activeTab, setActiveTab] = useState<'meta' | number>('meta');
  const [savedTeams, setSavedTeams] = useState<SavedTeamRef[]>([]);
  const [teamData, setTeamData] = useState<DashboardTeam | null>(null);
  const [teamErr, setTeamErr] = useState<string | null>(null);

  // Load the user's saved teams once on mount. 401 = anonymous (no tabs).
  useEffect(() => {
    fetch('/api/teams', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : { teams: [] }))
      .then((j) => setSavedTeams(j.teams ?? []))
      .catch(() => setSavedTeams([]));
  }, []);

  // Fetch /api/dashboards/meta whenever the Meta tab is active or its filters change.
  useEffect(() => {
    if (activeTab !== 'meta') return;
    const params = new URLSearchParams();
    params.set('source', source);
    if (days > 0) params.set('days', String(days));
    setData(null);
    fetch(`/api/dashboards/meta?${params}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => {
        if (j?.ok) setData(j as DashboardMeta);
        else setErr(j?.error ?? 'load_failed');
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [source, days, activeTab]);

  // Fetch team-aware dashboard data when a team tab is active.
  useEffect(() => {
    if (activeTab === 'meta') return;
    setTeamData(null);
    setTeamErr(null);
    fetch(`/api/dashboards/team/${activeTab}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => {
        if (j?.ok) setTeamData(j as DashboardTeam);
        else setTeamErr(j?.error ?? 'load_failed');
      })
      .catch((e) => setTeamErr(e instanceof Error ? e.message : String(e)));
  }, [activeTab]);

  if (err) {
    return (
      <div style={{ maxWidth: 1320, margin: '0 auto', padding: '24px' }}>
        <div className="mono" style={{ color: 'var(--neg)' }}>
          dashboard load failed: {err}
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: 1320,
        margin: '0 auto',
        padding: '20px 24px 60px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}
    >
      <Hero />
      <SuggestedPromptsRow />
      {savedTeams.length > 0 && (
        <TabBar
          tabs={[
            { id: 'meta', label: 'Meta' },
            ...savedTeams.map((t) => ({ id: t.id, label: t.name })),
          ]}
          active={activeTab}
          onPick={setActiveTab}
        />
      )}
      {activeTab !== 'meta' ? (
        <TeamPanels teamData={teamData} err={teamErr} />
      ) : (
        <MetaPanels data={data} source={source} days={days} onSource={setSource} onDays={setDays} />
      )}
    </div>
  );
}

function MetaPanels({
  data,
  source,
  days,
  onSource,
  onDays,
}: {
  data: DashboardMeta | null;
  source: 'showdown' | 'tournaments';
  days: 0 | 7 | 30 | 90;
  onSource: (s: 'showdown' | 'tournaments') => void;
  onDays: (d: 0 | 7 | 30 | 90) => void;
}) {
  return (
    <>
      <PageHeader regulation={data?.regulation_id ?? 'M-A'} kpis={data?.kpis ?? null} />
      <FilterBar source={source} days={days} onSource={onSource} onDays={onDays} />
      {!data ? (
        <div className="mono" style={{ color: 'var(--fg-4)', fontSize: 11, opacity: 0.6 }}>
          loading…
        </div>
      ) : (
        <>
          <KpiStrip kpis={data.kpis} sourceTagDisabled={source === 'tournaments'} />
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 16,
            }}
          >
            <Section
              title="Top leads"
              meta="who opens battles (turn 1)"
              right={
                <>
                  <span
                    className="mono caps"
                    style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
                  >
                    {data.top_leads.n_label}
                  </span>
                  <CitationBadge source="pokemon-showdown" />
                </>
              }
            >
              {data.top_leads.available ? (
                <LeadList rows={data.top_leads.results} />
              ) : (
                <div
                  className="mono"
                  style={{ padding: '14px', color: 'var(--fg-4)', fontSize: 11.5, lineHeight: 1.5 }}
                >
                  Lead data is only captured for Showdown ladder replays — switch the source filter
                  back to <b>Showdown</b> to see this panel.
                </div>
              )}
            </Section>
            <Section
              title="Top Pokémon overall"
              meta={source === 'tournaments' ? '% of tournament teams' : '% of team previews'}
              right={
                <>
                  <span
                    className="mono caps"
                    style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
                  >
                    {data.top_overall.n_label}
                  </span>
                  <CitationBadge
                    source={
                      data.top_overall.source === 'limitless' ? 'limitless' : 'pokemon-showdown'
                    }
                  />
                </>
              }
            >
              <LeadList rows={data.top_overall.results} />
            </Section>
          </div>
          <Section title="Meta speed tier" meta="modal spreads · level 50">
            <SpeedTierTable data={{ rows: data.speed_tier.rows } as any} />
          </Section>
          <div
            style={{
              display: 'grid',
              // Auto-stack to one column on mobile: each child needs >=320px or
              // it wraps onto its own row. The previous fixed `1.25fr 1fr`
              // grid stayed 2-column at iPhone width and the right pills /
              // dates overlapped neighbours.
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: 16,
            }}
          >
            <Section
              title="Recent tournaments"
              meta={`reg ${data.regulation_id}`}
              right={<CitationBadge source="limitless" />}
            >
              <TournamentList rows={data.recent_tournaments} />
            </Section>
            <Section title="Cited clips" meta="scraped transcripts">
              <ClipList rows={data.cited_clips} />
            </Section>
          </div>
        </>
      )}
    </>
  );
}

// ─── Tab bar + Team panels ────────────────────────────────────────

function TabBar({
  tabs,
  active,
  onPick,
}: {
  tabs: { id: 'meta' | number; label: string }[];
  active: 'meta' | number;
  onPick: (id: 'meta' | number) => void;
}) {
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {tabs.map((t) => {
        const isActive = active === t.id;
        return (
          <button
            key={String(t.id)}
            type="button"
            onClick={() => onPick(t.id)}
            className="caps mono"
            style={{
              padding: '6px 12px',
              fontSize: 10,
              fontWeight: 600,
              letterSpacing: '0.08em',
              background: isActive ? 'var(--accent-bg)' : 'transparent',
              border: `1px solid ${isActive ? 'var(--accent-line)' : 'var(--line-1)'}`,
              borderRadius: 4,
              color: isActive ? 'var(--accent-2)' : 'var(--fg-2)',
              cursor: 'pointer',
            }}
          >
            {t.label}
          </button>
        );
      })}
    </div>
  );
}

// `leadPairEndpoint` lets the community-team page reuse this whole panel
// set against `/api/dashboards/community-leadpair/:id` instead of the
// owner-only `/api/dashboards/leadpair/:teamId`. Defaults to the original.
export function TeamPanels({
  teamData,
  err,
  leadPairEndpoint,
}: {
  teamData: DashboardTeam | null;
  err: string | null;
  leadPairEndpoint?: string;
}) {
  if (err) {
    return (
      <div className="mono" style={{ color: 'var(--neg)', padding: '12px 0', fontSize: 12 }}>
        couldn't load team — {err}
      </div>
    );
  }
  if (!teamData) {
    return (
      <div className="mono" style={{ color: 'var(--fg-4)', fontSize: 11, opacity: 0.6 }}>
        loading team…
      </div>
    );
  }
  return (
    <>
      <TeamCard
        team={teamData.team.members as any}
        title={teamData.team.name}
        showdownExport={teamData.team.showdown_export}
        regulationId={teamData.regulation_id}
      />
      <TeamKpiStrip kpis={teamData.kpis} ohkoThreats={teamData.ohko_threats} />
      <Section
        title="→ Offence · your team hits top 5 meta"
        meta="default = best of your 4 moves; click a move chip to lock"
        right={<MatrixLegend />}
      >
        <DamageMatrixGrid
          matrix={teamData.damage_matrix_your_team_vs_meta}
          direction="offence"
          allowMoveToggle
        />
      </Section>
      <Section
        title="← Defence · top 5 meta hits you"
        meta="each threat using its most-used move"
        right={<MatrixLegend />}
      >
        <DamageMatrixGrid
          matrix={teamData.damage_matrix_meta_vs_your_team}
          direction="defence"
          showMoveHeaders
        />
      </Section>
      <LeadPairDashboard
        teamId={teamData.team.id}
        members={teamData.team.members as any[]}
        endpoint={leadPairEndpoint}
      />
      <Section
        title="Predicted opponent leads"
        meta={`based on ${teamData.predicted_leads.matchups_used} similar matchups`}
        right={<CitationBadge source="pokemon-showdown" />}
      >
        {teamData.predicted_leads.predictions.length === 0 ? (
          <div
            className="mono"
            style={{ padding: '14px', color: 'var(--fg-4)', fontSize: 11.5, lineHeight: 1.5 }}
          >
            No similar-team replays in the corpus yet — once more replays are indexed against teams
            sharing ≥4 species with yours, predicted leads will land here.
          </div>
        ) : (
          <LeadPredictionCard
            data={{
              matchups_used: teamData.predicted_leads.matchups_used,
              exact_matchups: teamData.predicted_leads.exact_matchups,
              predictions: teamData.predicted_leads.predictions,
            }}
          />
        )}
      </Section>
      <Section title="Speed tier" meta="your team highlighted against the meta">
        <SpeedTierTable
          data={{ rows: teamData.speed_tier.rows } as any}
          highlightSpecies={teamData.speed_tier.team_species}
        />
      </Section>
      <Section
        title="Similar tournament teams"
        meta={`${teamData.similar_teams.teams_matched} teams · ≥4 of 6 species shared`}
        right={<CitationBadge source="limitless" />}
      >
        <SimilarTeamsList precedents={teamData.similar_teams.precedents} />
      </Section>
    </>
  );
}

function TeamKpiStrip({
  kpis,
  ohkoThreats,
}: {
  kpis: DashboardTeam['kpis'];
  ohkoThreats: DashboardTeam['ohko_threats'];
}) {
  const ohkoSub =
    ohkoThreats.length > 0
      ? ohkoThreats
          .slice(0, 2)
          .map((t) => `${t.move} → ${prettySpecies(t.defender)}`)
          .join(' · ')
      : 'no guaranteed OHKOs from top-5 meta';
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
      <Kpi
        label="Similar teams in corpus"
        value={kpis.similar_teams_in_corpus.toLocaleString()}
        sub="≥4 of 6 species shared"
      />
      <Kpi
        label="Replays studied"
        value={kpis.replays_studied.toLocaleString()}
        sub="aggregated across similar teams"
      />
      <Kpi
        label="Exact-team matchups"
        value={kpis.exact_matchups.toLocaleString()}
        sub="all 6 species identical"
      />
      <Kpi label="OHKO threats incoming" value={ohkoThreats.length.toString()} sub={ohkoSub} />
    </div>
  );
}

// DamageMatrixGrid + MatrixLegend live in src/web/components/DamageMatrixGrid.tsx.

// ─── Dashboard C: 2v2 lead-pair matchup ────────────────────────────

function LeadPairDashboard({
  teamId,
  members,
  endpoint,
}: {
  teamId: number;
  members: any[];
  endpoint?: string;
}) {
  // Picker state: 2 species per side. Defaults filled from the server on
  // first response.
  const [yours, setYours] = useState<[string, string] | null>(null);
  const [theirs, setTheirs] = useState<[string, string] | null>(null);
  const [data, setData] = useState<DashboardLeadPair | null>(null);
  const [err, setErr] = useState<string | null>(null);

  // `endpoint` is the full URL minus the query string; defaults to the
  // owner-gated saved-team endpoint. Community-team callers pass
  // `/api/dashboards/community-leadpair/:id`.
  const baseUrl = endpoint ?? `/api/dashboards/leadpair/${teamId}`;

  useEffect(() => {
    const params = new URLSearchParams();
    if (yours) params.set('yours', yours.join(','));
    if (theirs) params.set('theirs', theirs.join(','));
    setData(null);
    setErr(null);
    fetch(`${baseUrl}?${params}`, { credentials: 'include' })
      .then((r) => r.json())
      .then((j) => {
        if (j?.ok) {
          setData(j as DashboardLeadPair);
          // Lock state to whatever the server resolved (defaults for first load).
          if (!yours)
            setYours([j.yours[0]?.species ?? '', j.yours[1]?.species ?? ''] as [string, string]);
          if (!theirs)
            setTheirs([j.theirs[0]?.species ?? '', j.theirs[1]?.species ?? ''] as [string, string]);
        } else {
          setErr(j?.error ?? 'load_failed');
        }
      })
      .catch((e) => setErr(e instanceof Error ? e.message : String(e)));
  }, [baseUrl, yours, theirs]);

  const teamSpeciesOptions = members
    .map((m: any) => String(m?.species ?? ''))
    .filter((s) => s.length > 0);

  return (
    <>
      <Section title="2v2 lead-pair matchup" meta="pick two on each side · 4-mon speed order">
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            gap: 16,
            padding: 14,
            alignItems: 'center',
          }}
        >
          <PairPicker
            label="Your pair"
            options={teamSpeciesOptions}
            value={yours}
            onChange={setYours}
          />
          <span
            className="caps mono"
            style={{ color: 'var(--fg-3)', fontSize: 11, letterSpacing: '0.1em' }}
          >
            vs
          </span>
          <PairPicker
            label="Their pair"
            options={(data?.opp_picker_options ?? []).map((o) => o.species)}
            value={theirs}
            onChange={setTheirs}
          />
        </div>
      </Section>

      {err && (
        <div className="mono" style={{ color: 'var(--neg)', fontSize: 12 }}>
          lead-pair load failed: {err}
        </div>
      )}
      {data && (
        <>
          {data.opp_sets.length > 0 && (
            <Section
              title="Opponent · most-used sets"
              meta="Pikalytics modal builds"
              right={<CitationBadge source="pikalytics" />}
            >
              <OppSetsGrid sets={data.opp_sets} />
            </Section>
          )}
          {data.speed_order.length > 0 && (
            <Section title="Speed order · 4 mons" meta="fast → slow · with field toggles">
              <SpeedMiniList rows={data.speed_order} />
            </Section>
          )}
          <Section
            title="→ Offence · your pair hits theirs"
            meta="best of your moves per cell"
            right={<MatrixLegend />}
          >
            <DamageMatrixGrid matrix={data.matrix_offence} direction="offence" allowMoveToggle />
          </Section>
          <Section
            title="← Defence · their pair hits yours"
            meta="each threat using its most-used move"
            right={<MatrixLegend />}
          >
            <DamageMatrixGrid matrix={data.matrix_defence} direction="defence" showMoveHeaders />
          </Section>
          {data.first_moves.some((f) => f.total_appearances > 0) && (
            <Section
              title="First-move usage · opposing pair"
              meta="what each threat actually clicks turn 1"
              right={<CitationBadge source="pokemon-showdown" />}
            >
              <FirstMovesGrid data={data.first_moves} />
            </Section>
          )}
          {data.annotated_replays.length > 0 && (
            <Section
              title="Cited replays"
              meta={`${data.annotated_replays.length} annotated replays with this lead pair`}
              right={<CitationBadge source="pokemon-showdown" />}
            >
              <AnnotatedReplaysList rows={data.annotated_replays} />
            </Section>
          )}
        </>
      )}
    </>
  );
}

function PairPicker({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: string[];
  value: [string, string] | null;
  onChange: (v: [string, string]) => void;
}) {
  const a = value?.[0] ?? '';
  const b = value?.[1] ?? '';
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span
        className="caps mono"
        style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
      >
        {label}
      </span>
      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        <PairSelect value={a} options={options} onChange={(v) => onChange([v, b])} />
        <span style={{ color: 'var(--fg-4)' }}>+</span>
        <PairSelect value={b} options={options} onChange={(v) => onChange([a, v])} />
      </div>
    </div>
  );
}

function PairSelect({
  value,
  options,
  onChange,
}: {
  value: string;
  options: string[];
  onChange: (v: string) => void;
}) {
  // Allow the current value even if not in options (e.g. server resolved to a
  // species the picker doesn't have — keeps the form valid).
  const opts = options.includes(value) || !value ? options : [value, ...options];
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mono"
        style={{
          fontSize: 12,
          padding: '4px 8px',
          background: 'var(--bg-2)',
          color: 'var(--fg-1)',
          border: '1px solid var(--line-1)',
          borderRadius: 3,
          minWidth: 130,
        }}
      >
        {opts.map((opt) => (
          <option key={opt} value={opt}>
            {prettySpecies(opt)}
          </option>
        ))}
      </select>
      <SpeciesTypeChips species={value} />
    </div>
  );
}

function SpeciesTypeChips({ species }: { species: string }) {
  // Resolve types from the MONS registry. Try the species id verbatim first,
  // then a lowercased / mega-stripped variant. The registry uses lowercase ids.
  const tries = [species.toLowerCase(), species.toLowerCase().replace(/-mega(-[xy])?$/i, '')];
  let types: readonly string[] | undefined;
  for (const t of tries) {
    if (MONS[t]) {
      types = MONS[t].types;
      break;
    }
  }
  if (!types || types.length === 0) return null;
  return (
    <div style={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
      {types.map((t) => (
        <TypeChip key={t} type={t} size="sm" />
      ))}
    </div>
  );
}

function SpeedMiniList({
  rows,
}: {
  rows: { species: string; label: string; final_speed: number; side: 'yours' | 'theirs' }[];
}) {
  // Field toggles — Tailwind doubles speed for one side (you/them/none);
  // Trick Room inverts the order so the slowest moves first. Sun is intentionally
  // omitted from v1 because it depends on per-species abilities (Chlorophyll
  // etc.) which require @pkmn/dex lookups we don't have wired here.
  const [tailwind, setTailwind] = useState<'none' | 'yours' | 'theirs'>('none');
  const [trickRoom, setTrickRoom] = useState(false);

  const adjusted = rows.map((r) => {
    let speed = r.final_speed;
    if (tailwind === 'yours' && r.side === 'yours') speed = Math.floor(speed * 2);
    if (tailwind === 'theirs' && r.side === 'theirs') speed = Math.floor(speed * 2);
    return { ...r, speed };
  });
  adjusted.sort((a, b) => (trickRoom ? a.speed - b.speed : b.speed - a.speed));

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '8px 14px',
          borderBottom: '1px solid var(--line-1)',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span
            className="caps mono"
            style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
          >
            tailwind
          </span>
          {(['none', 'yours', 'theirs'] as const).map((mode) => (
            <Pill key={mode} active={tailwind === mode} onClick={() => setTailwind(mode)}>
              {mode === 'none' ? 'off' : mode}
            </Pill>
          ))}
        </div>
        <Pill active={trickRoom} onClick={() => setTrickRoom((v) => !v)}>
          trick room {trickRoom ? 'on' : 'off'}
        </Pill>
      </div>
      {adjusted.map((r, i) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: speed order is positional — same species can appear on both sides (mirror), index disambiguates
          key={`${r.species}-${i}`}
          style={{
            display: 'grid',
            gridTemplateColumns: '28px 32px 1fr 80px',
            alignItems: 'center',
            gap: 10,
            padding: '7px 14px',
            borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
            borderLeft: r.side === 'yours' ? '2px solid var(--accent-1)' : 'none',
          }}
        >
          <span
            className="mono tabular"
            style={{ fontSize: 11, color: 'var(--fg-4)', textAlign: 'right' }}
          >
            {i + 1}
          </span>
          <Sprite species={r.species as SpeciesId} size={28} />
          <span style={{ fontSize: 13, color: 'var(--fg-1)' }}>
            {prettySpecies(r.label)}
            <span
              className="caps mono"
              style={{ color: 'var(--fg-4)', fontSize: 9, marginLeft: 6, letterSpacing: '0.08em' }}
            >
              {r.side === 'yours' ? 'you' : 'them'}
            </span>
          </span>
          <span
            className="mono tabular"
            style={{ fontSize: 14, fontWeight: 600, color: 'var(--fg-1)', textAlign: 'right' }}
          >
            {r.speed}
            {r.speed !== r.final_speed && (
              <span style={{ color: 'var(--accent-2)', fontSize: 9, marginLeft: 4 }}>
                (was {r.final_speed})
              </span>
            )}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Opponent set cards ──────────────────────────────────────────────

function OppSetsGrid({ sets }: { sets: DashboardLeadPair['opp_sets'] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 12,
        padding: 12,
      }}
    >
      {sets.map((s) => (
        <div
          key={s.species}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: 10,
            background: 'var(--bg-2)',
            border: '1px solid var(--line-1)',
            borderRadius: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sprite species={s.species as SpeciesId} size={32} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 13, color: 'var(--fg-1)', fontWeight: 500 }}>
                {prettySpecies(s.species)}
              </span>
              {s.usage_pct != null && (
                <span className="mono caps" style={{ fontSize: 9, color: 'var(--fg-4)' }}>
                  {s.usage_pct.toFixed(1)}% usage · modal
                </span>
              )}
            </div>
          </div>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: '60px 1fr',
              gap: 4,
              fontSize: 11,
              color: 'var(--fg-2)',
            }}
          >
            <span className="mono caps" style={{ color: 'var(--fg-4)' }}>
              item
            </span>
            <span>{s.item ?? '—'}</span>
            <span className="mono caps" style={{ color: 'var(--fg-4)' }}>
              ability
            </span>
            <span>{s.ability ?? '—'}</span>
            <span className="mono caps" style={{ color: 'var(--fg-4)' }}>
              nature
            </span>
            <span>{s.nature ?? '—'}</span>
            <span className="mono caps" style={{ color: 'var(--fg-4)' }}>
              evs
            </span>
            <span className="mono tabular" style={{ fontSize: 10.5 }}>
              {s.ev_spread ?? '—'}
            </span>
          </div>
          {s.moves.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3, marginTop: 4 }}>
              {s.moves.map((m) => (
                <div
                  key={m.move}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 50px 36px',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 10.5,
                  }}
                >
                  <span style={{ color: 'var(--fg-2)' }}>{m.move}</span>
                  <div
                    style={{
                      position: 'relative',
                      height: 4,
                      background: 'var(--bg-3)',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        right: 'auto',
                        width: `${Math.min(100, m.pct)}%`,
                        background: 'var(--accent-1)',
                      }}
                    />
                  </div>
                  <span
                    className="mono tabular"
                    style={{ color: 'var(--fg-3)', textAlign: 'right' }}
                  >
                    {m.pct.toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── First-move usage bars ──────────────────────────────────────────

function FirstMovesGrid({ data }: { data: DashboardLeadPair['first_moves'] }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
        gap: 12,
        padding: 12,
      }}
    >
      {data.map((entry) => (
        <div
          key={entry.species}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 6,
            padding: 10,
            background: 'var(--bg-2)',
            border: '1px solid var(--line-1)',
            borderRadius: 4,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sprite species={entry.species as SpeciesId} size={28} />
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <span style={{ fontSize: 13, color: 'var(--fg-1)' }}>
                {prettySpecies(entry.species)}
              </span>
              <span className="mono caps" style={{ fontSize: 9, color: 'var(--fg-4)' }}>
                n={entry.total_appearances} replays
              </span>
            </div>
          </div>
          {entry.moves.length === 0 ? (
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-4)' }}>
              never observed in corpus
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {entry.moves.map((m) => (
                <div
                  key={m.move}
                  style={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 50px 40px',
                    alignItems: 'center',
                    gap: 6,
                    fontSize: 10.5,
                  }}
                >
                  <span
                    style={{
                      color: m.move === '(no_move)' ? 'var(--fg-4)' : 'var(--fg-2)',
                      fontStyle: m.move === '(no_move)' ? 'italic' : 'normal',
                    }}
                  >
                    {m.move}
                  </span>
                  <div
                    style={{
                      position: 'relative',
                      height: 4,
                      background: 'var(--bg-3)',
                      borderRadius: 2,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        position: 'absolute',
                        inset: 0,
                        right: 'auto',
                        width: `${m.fraction * 100}%`,
                        background: 'var(--accent-1)',
                      }}
                    />
                  </div>
                  <span
                    className="mono tabular"
                    style={{ color: 'var(--fg-3)', textAlign: 'right' }}
                  >
                    {(m.fraction * 100).toFixed(0)}%
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function AnnotatedReplaysList({ rows }: { rows: DashboardLeadPair['annotated_replays'] }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {rows.map((r, i) => (
        <a
          // biome-ignore lint/suspicious/noArrayIndexKey: replay URL can repeat if a replay has multiple annotations; index disambiguates.
          key={`${r.replay_url}-${i}`}
          href={r.replay_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 4,
            padding: '10px 14px',
            borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {r.winning_archetype && (
              <span
                className="caps mono"
                style={{
                  fontSize: 9,
                  color: 'var(--accent-2)',
                  background: 'var(--accent-bg)',
                  border: '1px solid var(--accent-line)',
                  borderRadius: 2,
                  padding: '1px 6px',
                  letterSpacing: '0.06em',
                }}
              >
                {r.winning_archetype}
              </span>
            )}
            {r.quality_rating != null && (
              <span
                className="mono caps"
                style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.06em' }}
              >
                q={r.quality_rating}
              </span>
            )}
            {r.led_by && (
              <span
                className="mono caps"
                style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.06em' }}
              >
                led by {r.led_by}
              </span>
            )}
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: 'var(--fg-2)',
              lineHeight: 1.45,
              fontStyle: 'italic',
            }}
          >
            "{r.summary}"
          </div>
          {r.matchup_takeaway && (
            <div style={{ fontSize: 11.5, color: 'var(--fg-3)', lineHeight: 1.4 }}>
              {r.matchup_takeaway}
            </div>
          )}
        </a>
      ))}
    </div>
  );
}

// cellTooltip lives inside src/web/components/DamageMatrixGrid.tsx now.

function SimilarTeamsList({
  precedents,
}: {
  precedents: DashboardTeam['similar_teams']['precedents'];
}) {
  if (precedents.length === 0) {
    return (
      <div
        className="mono"
        style={{ padding: '14px', color: 'var(--fg-4)', fontSize: 11.5, lineHeight: 1.5 }}
      >
        No tournament teams in our corpus share ≥4 species with yours yet.
      </div>
    );
  }
  return (
    <div>
      {precedents.map((p, i) => (
        <a
          // biome-ignore lint/suspicious/noArrayIndexKey: same teamId can appear under multiple tournament entries (player+placement) — the i suffix is needed to keep keys unique.
          key={`${p.teamId}-${i}`}
          href={p.sourceUrl ?? '#'}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'grid',
            gridTemplateColumns: '50px 1fr auto auto',
            gap: 12,
            alignItems: 'center',
            padding: '10px 14px',
            borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <span
            className="mono tabular caps"
            style={{
              fontSize: 9,
              color: p.placement && p.placement <= 8 ? 'var(--accent-2)' : 'var(--fg-3)',
              background: p.placement && p.placement <= 8 ? 'var(--accent-bg)' : 'var(--bg-2)',
              border: `1px solid ${
                p.placement && p.placement <= 8 ? 'var(--accent-line)' : 'var(--line-1)'
              }`,
              padding: '2px 6px',
              borderRadius: 2,
              letterSpacing: '0.06em',
              textAlign: 'center',
            }}
          >
            {p.placement != null ? `#${p.placement}` : '—'}
          </span>
          <div style={{ minWidth: 0 }}>
            <div style={{ fontSize: 13, color: 'var(--fg-1)' }}>
              {p.playerHandle ?? 'unknown'}
              {p.tournamentName ? (
                <span style={{ color: 'var(--fg-3)' }}> · {p.tournamentName}</span>
              ) : null}
            </div>
            <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-4)', marginTop: 2 }}>
              {p.tournamentDate ?? '—'}
            </div>
          </div>
          <span
            className="mono caps"
            style={{
              fontSize: 9,
              color: 'var(--fg-3)',
              letterSpacing: '0.06em',
              padding: '2px 6px',
              border: '1px solid var(--line-1)',
              borderRadius: 2,
            }}
          >
            {p.sharedSpecies}/6 shared
          </span>
          <span className="mono caps" style={{ fontSize: 9, color: 'var(--fg-3)' }}>
            view team →
          </span>
        </a>
      ))}
    </div>
  );
}

// ─── Hero + Suggested prompts (merged in from the old EmptyState) ───
//
// The dashboard IS the home page now. Hero is intentionally compressed —
// the cold-open value is the meta data below, not a marketing pitch.

function Hero() {
  const navTo = (path: string) => {
    window.history.pushState({}, '', path);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  return (
    <header style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <h1
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 'clamp(24px, 4vw, 32px)',
          fontWeight: 400,
          letterSpacing: '-0.012em',
          lineHeight: 1.12,
          margin: 0,
          color: 'var(--fg-1)',
        }}
      >
        Plan your next team. Theorymon out loud.
      </h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span
          style={{
            fontFamily: 'var(--font-serif)',
            color: 'var(--accent-2)',
            fontSize: 14,
            background: 'var(--accent-bg)',
            border: '1px solid var(--accent-line)',
            padding: '4px 10px',
            borderRadius: 4,
          }}
        >
          {ACTIVE_REGULATION.label}
        </span>
        <span style={{ color: 'var(--fg-3)', fontSize: 12.5 }}>{ACTIVE_REGULATION.note}</span>
      </div>
      {/* Primary nav — the dashboard is the landing surface, but Chat and
          Build are the two interactive paths that earn the page beyond
          "read meta numbers". Pinning them in the hero so mobile users
          aren't hunting for an entry point. */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 4 }}>
        <button
          type="button"
          onClick={() => navTo('/chat')}
          className="caps mono"
          style={{
            fontSize: 11,
            padding: '8px 16px',
            background: 'var(--accent-1)',
            color: 'var(--bg-0)',
            border: '1px solid var(--accent-line)',
            borderRadius: 3,
            letterSpacing: '0.08em',
            cursor: 'pointer',
          }}
        >
          → ask vgcbuddy (chat)
        </button>
        <button
          type="button"
          onClick={() => navTo('/build')}
          className="caps mono"
          style={{
            fontSize: 11,
            padding: '8px 16px',
            background: 'var(--accent-bg)',
            color: 'var(--accent-2)',
            border: '1px solid var(--accent-line)',
            borderRadius: 3,
            letterSpacing: '0.08em',
            cursor: 'pointer',
          }}
        >
          → team builder
        </button>
      </div>
    </header>
  );
}

function SuggestedPromptsRow() {
  const onPick = (prompt: string) => {
    window.history.pushState({}, '', `/chat?q=${encodeURIComponent(prompt)}`);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  return (
    <section>
      <div
        className="caps mono"
        style={{
          fontSize: 9,
          color: 'var(--fg-4)',
          letterSpacing: '0.1em',
          marginBottom: 8,
        }}
      >
        suggested prompts
      </div>
      <div className="prompt-grid">
        {SUGGESTED_PROMPTS.map((p, i) => (
          <button
            key={p}
            type="button"
            onClick={() => onPick(p)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '10px 12px',
              background: 'var(--bg-1)',
              border: '1px solid var(--line-1)',
              borderRadius: 4,
              fontSize: 13,
              color: 'var(--fg-2)',
              textAlign: 'left',
              cursor: 'pointer',
            }}
          >
            <span
              className="mono"
              style={{ color: 'var(--fg-4)', fontSize: 10, letterSpacing: '0.08em' }}
            >
              0{i + 1}
            </span>
            <span style={{ flex: 1 }}>{p}</span>
            <span style={{ color: 'var(--fg-4)' }}>→</span>
          </button>
        ))}
      </div>
    </section>
  );
}

// ─── Filter bar ───────────────────────────────────────────────────

function FilterBar({
  source,
  days,
  onSource,
  onDays,
}: {
  source: 'showdown' | 'tournaments';
  days: 0 | 7 | 30 | 90;
  onSource: (s: 'showdown' | 'tournaments') => void;
  onDays: (d: 0 | 7 | 30 | 90) => void;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 16,
        alignItems: 'center',
        padding: '8px 12px',
        background: 'var(--bg-1)',
        border: '1px solid var(--line-1)',
        borderRadius: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          className="caps mono"
          style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
        >
          source
        </span>
        <Pill active={source === 'showdown'} onClick={() => onSource('showdown')}>
          Showdown
        </Pill>
        <Pill active={source === 'tournaments'} onClick={() => onSource('tournaments')}>
          Tournaments
        </Pill>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span
          className="caps mono"
          style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.08em' }}
        >
          window
        </span>
        {([7, 30, 90, 0] as const).map((d) => (
          <Pill key={d} active={days === d} onClick={() => onDays(d)}>
            {d === 0 ? 'all-time' : `${d}d`}
          </Pill>
        ))}
      </div>
    </div>
  );
}

function Pill({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="caps mono"
      style={{
        padding: '4px 10px',
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: '0.06em',
        background: active ? 'var(--accent-bg)' : 'transparent',
        border: `1px solid ${active ? 'var(--accent-line)' : 'var(--line-1)'}`,
        borderRadius: 3,
        color: active ? 'var(--accent-2)' : 'var(--fg-2)',
        cursor: 'pointer',
      }}
    >
      {children}
    </button>
  );
}

// ─────────────────────────────────────────────────────────────────────

function PageHeader({
  regulation,
  kpis,
}: {
  regulation: string;
  kpis: DashboardMeta['kpis'] | null;
}) {
  // Hero handles the title — this header carries only the "or just browse"
  // meta-overview section label + corpus-size sub-text. Compressed because
  // the Hero above already names the regulation.
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <div
        className="caps mono"
        style={{ fontSize: 9.5, color: 'var(--accent-2)', letterSpacing: '0.14em' }}
      >
        meta overview · reg {regulation}
      </div>
      <div
        style={{
          color: 'var(--fg-3)',
          fontSize: 13,
          lineHeight: 1.5,
          maxWidth: '68ch',
        }}
      >
        {kpis ? kpis.replays_indexed.toLocaleString() : '…'} ranked replays ·{' '}
        {kpis ? kpis.tournament_entries.toLocaleString() : '…'} tournament entries indexed. Cold
        numbers, no editorializing — every row is calc-verified or directly cited.
      </div>
    </div>
  );
}

function KpiStrip({
  kpis,
  sourceTagDisabled,
}: {
  kpis: DashboardMeta['kpis'];
  sourceTagDisabled: boolean;
}) {
  // When source=tournaments, the lead-related KPIs aren't computable. Show
  // "—" with a clarifying sub-text rather than zero/blank — keeps the four
  // tiles laid out consistently.
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
      <Kpi
        label="Replays indexed"
        value={kpis.replays_indexed.toLocaleString()}
        sub="Showdown ranked corpus"
      />
      <Kpi
        label="Tournament entries"
        value={kpis.tournament_entries.toLocaleString()}
        sub="across all M-A events"
      />
      <Kpi
        label="Most-used lead"
        value={kpis.top_lead ? fmtPct(kpis.top_lead.pct) : '—'}
        valueSuffix={kpis.top_lead ? '%' : undefined}
        sub={
          kpis.top_lead
            ? `${prettySpecies(kpis.top_lead.species)} · ${kpis.top_lead.count} of ${kpis.top_lead.of}`
            : sourceTagDisabled
              ? 'Showdown source only'
              : 'no replays indexed'
        }
      />
      <Kpi
        label="Most-used lead pair"
        value={kpis.top_lead_pair ? fmtPct(kpis.top_lead_pair.pct) : '—'}
        valueSuffix={kpis.top_lead_pair ? '%' : undefined}
        sub={
          kpis.top_lead_pair
            ? `${prettySpecies(kpis.top_lead_pair.pair[0])} + ${prettySpecies(kpis.top_lead_pair.pair[1])} · ${kpis.top_lead_pair.count} of ${kpis.top_lead_pair.of}`
            : sourceTagDisabled
              ? 'Showdown source only'
              : 'no replays indexed'
        }
      />
    </div>
  );
}

function Kpi({
  label,
  value,
  valueSuffix,
  sub,
}: {
  label: string;
  value: string;
  valueSuffix?: string;
  sub?: string;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-1)',
        border: '1px solid var(--line-1)',
        borderRadius: 6,
        padding: '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <div
        className="caps mono"
        style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.10em' }}
      >
        {label}
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4, marginTop: 4 }}>
        <span
          className="mono tabular"
          style={{
            fontSize: 24,
            fontWeight: 600,
            lineHeight: 1.1,
            color: 'var(--fg-1)',
            letterSpacing: '-0.01em',
          }}
        >
          {value}
        </span>
        {valueSuffix && (
          <span className="mono" style={{ fontSize: 11, color: 'var(--fg-3)' }}>
            {valueSuffix}
          </span>
        )}
      </div>
      {sub && (
        <div style={{ color: 'var(--fg-3)', fontSize: 11.5, marginTop: 4, lineHeight: 1.4 }}>
          {sub}
        </div>
      )}
    </div>
  );
}

function Section({
  title,
  meta,
  right,
  children,
}: {
  title: string;
  meta?: string;
  right?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      style={{
        background: 'var(--bg-1)',
        border: '1px solid var(--line-1)',
        borderRadius: 6,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
      }}
    >
      <header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          padding: '10px 14px',
          borderBottom: '1px solid var(--line-1)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, minWidth: 0 }}>
          <span
            className="caps"
            style={{
              fontSize: 10.5,
              color: 'var(--fg-2)',
              letterSpacing: '0.08em',
              fontWeight: 600,
            }}
          >
            {title}
          </span>
          {meta && (
            <span className="mono" style={{ fontSize: 10, color: 'var(--fg-4)' }}>
              {meta}
            </span>
          )}
        </div>
        {right && <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{right}</div>}
      </header>
      <div>{children}</div>
    </section>
  );
}

// ─── Top Leads / Top Overall list ───────────────────────────────────

function LeadList({ rows }: { rows: LeadRow[] }) {
  if (rows.length === 0) {
    return (
      <div
        className="mono"
        style={{
          padding: '14px',
          color: 'var(--fg-4)',
          fontSize: 11.5,
        }}
      >
        no data — ingestion empty for this source.
      </div>
    );
  }
  const max = rows[0].fraction;
  return (
    <div>
      {rows.map((r, i) => (
        <LeadRow key={r.species} row={r} rank={i + 1} max={max} first={i === 0} />
      ))}
    </div>
  );
}

function LeadRow({
  row,
  rank,
  max,
  first,
}: {
  row: LeadRow;
  rank: number;
  max: number;
  first: boolean;
}) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '28px 32px 1fr 1.6fr 60px 60px',
        alignItems: 'center',
        gap: 10,
        padding: '7px 14px',
        borderTop: first ? 'none' : '1px solid var(--line-1)',
      }}
    >
      <span
        className="mono tabular"
        style={{ fontSize: 11, color: 'var(--fg-4)', textAlign: 'right' }}
      >
        {rank}
      </span>
      <Sprite species={row.species as SpeciesId} size={28} />
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: 'var(--fg-1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {prettySpecies(row.species)}
        </div>
      </div>
      <div
        style={{
          position: 'relative',
          height: 5,
          background: 'var(--bg-3)',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            right: 'auto',
            width: `${(row.fraction / max) * 100}%`,
            background: 'var(--accent-1)',
          }}
        />
      </div>
      <span
        className="mono tabular"
        style={{ fontSize: 14, fontWeight: 600, textAlign: 'right', color: 'var(--fg-1)' }}
      >
        {fmtPct(row.fraction)}%
      </span>
      <span
        className="mono tabular"
        style={{ fontSize: 11, color: 'var(--fg-4)', textAlign: 'right' }}
      >
        n={row.count}
      </span>
    </div>
  );
}

// ─── Recent Tournaments panel ───────────────────────────────────────

function TournamentList({ rows }: { rows: TournamentRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="mono" style={{ padding: '14px', color: 'var(--fg-4)', fontSize: 11.5 }}>
        no tournaments indexed for this regulation yet.
      </div>
    );
  }
  return (
    <div>
      {rows.map((t, i) => (
        <a
          key={t.id}
          href={t.source_url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            padding: '10px 14px',
            borderTop: i === 0 ? 'none' : '1px solid var(--line-1)',
            textDecoration: 'none',
            color: 'inherit',
          }}
        >
          <span
            className="mono tabular caps"
            style={{
              fontSize: 9,
              color: 'var(--accent-2)',
              background: 'var(--accent-bg)',
              border: '1px solid var(--accent-line)',
              padding: '2px 6px',
              borderRadius: 2,
              letterSpacing: '0.06em',
              minWidth: 40,
              textAlign: 'center',
            }}
          >
            1st
          </span>
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                fontSize: 13,
                fontWeight: 500,
                color: 'var(--fg-1)',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t.name}
            </div>
            <div
              style={{
                fontSize: 11.5,
                color: 'var(--fg-3)',
                marginTop: 2,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t.winner_handle ?? 'winner unknown'}
              {t.winner_country ? ` · ${t.winner_country}` : ''}
            </div>
          </div>
          {/* flexShrink: 0 stops the right column collapsing under the left
              column's ellipsis on narrow widths — the previous design let
              "47 PLAYERS" overlap onto "wochienreaction · BR" at iPhone width. */}
          <div style={{ textAlign: 'right', flexShrink: 0 }}>
            <div className="mono tabular" style={{ fontSize: 11, color: 'var(--fg-2)' }}>
              {formatDateShort(t.date)}
            </div>
            <div
              className="mono caps"
              style={{ fontSize: 9, color: 'var(--fg-4)', letterSpacing: '0.06em', marginTop: 1 }}
            >
              {t.total_players != null ? `${t.total_players} players` : '—'}
            </div>
          </div>
        </a>
      ))}
    </div>
  );
}

// ─── Cited Clips panel ──────────────────────────────────────────────

function ClipList({ rows }: { rows: ClipRow[] }) {
  if (rows.length === 0) {
    return (
      <div className="mono" style={{ padding: '14px', color: 'var(--fg-4)', fontSize: 11.5 }}>
        no clips extracted for this regulation yet.
      </div>
    );
  }
  return (
    <div style={{ padding: '10px 14px', display: 'flex', flexDirection: 'column', gap: 8 }}>
      {rows.map((c) => {
        const hasUrl = !!c.source_url;
        const ts = c.source_timestamp_sec;
        const tsSuffix = ts != null && hasUrl ? `&t=${Math.max(0, Math.floor(ts))}s` : '';
        const href = hasUrl ? `${c.source_url}${tsSuffix}` : undefined;
        const truncated = c.quote.length > 140 ? `${c.quote.slice(0, 138)}…` : c.quote;
        const body = (
          <>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                marginBottom: 4,
                flexWrap: 'wrap',
              }}
            >
              <span
                className="caps mono"
                style={{
                  fontSize: 8.5,
                  color: 'var(--fg-3)',
                  letterSpacing: '0.08em',
                  padding: '1px 5px',
                  border: '1px solid var(--line-1)',
                  borderRadius: 2,
                }}
              >
                {c.claim_type.replace(/_/g, ' ')}
              </span>
              {c.channel_name && (
                <span className="mono" style={{ fontSize: 10, color: 'var(--fg-3)' }}>
                  {c.channel_name}
                </span>
              )}
              {ts != null && (
                <span className="mono tabular" style={{ fontSize: 10, color: 'var(--fg-4)' }}>
                  {formatTimestamp(ts)}
                </span>
              )}
            </div>
            <div
              style={{
                fontSize: 12,
                color: 'var(--fg-2)',
                lineHeight: 1.45,
                fontStyle: 'italic',
              }}
            >
              "{truncated}"
            </div>
          </>
        );
        const sharedStyle: React.CSSProperties = {
          padding: '8px 10px',
          background: 'var(--bg-untrusted, var(--bg-2))',
          border: '1px solid var(--line-1)',
          borderLeft: '2px solid #2e4453',
          borderRadius: 4,
          textDecoration: 'none',
          color: 'inherit',
        };
        return href ? (
          <a
            key={c.id}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...sharedStyle, cursor: 'pointer' }}
          >
            {body}
          </a>
        ) : (
          <div key={c.id} style={sharedStyle}>
            {body}
          </div>
        );
      })}
    </div>
  );
}

// ─── helpers ───────────────────────────────────────────────────────

function fmtPct(fraction: number): string {
  return (fraction * 100).toFixed(1);
}

function formatDateShort(iso: string): string {
  const d = new Date(iso);
  if (!Number.isFinite(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: '2-digit' });
}

function formatTimestamp(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

function prettySpecies(id: string): string {
  return id
    .split('-')
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(' ');
}
